import { createTheme, type Theme } from "@mui/material/styles";
import { pink, lightTokens, darkTokens } from "./tokens";

// Custom breakpoints (420/480/576/768/767.98) used throughout styles.css
// don't map cleanly onto MUI's 5-slot xs/sm/md/lg/xl system - these values
// are only an approximation for MUI-internal responsive behavior (Dialog
// fullscreen breakpoints, etc). Anything that must match a legacy
// breakpoint pixel-for-pixel should use a raw
// sx={{ "@media (max-width:420px)": {...} }} instead.
export function buildMuiTheme(mode: "light" | "dark"): Theme {
  const t = mode === "dark" ? darkTokens : lightTokens;

  return createTheme({
    palette: {
      mode,
      primary: { main: pink[500], light: pink[400], dark: pink[600], contrastText: "#fff" },
      secondary: { main: pink[700] },
      // Matches Bootstrap's --bs-danger exactly (styles.css has no custom
      // .btn-danger override, so it's the plain Bootstrap default) -
      // MUI's own default error red is a close but different shade.
      error: { main: "#dc3545" },
      background: { default: t.bodyBgSolid },
      text: { primary: t.ink, secondary: t.muted },
    },
    shape: { borderRadius: 18 }, // --radius
    typography: {
      fontFamily:
        '"SF Pro Display", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    },
    breakpoints: { values: { xs: 0, sm: 480, md: 768, lg: 992, xl: 1200 } },
    // MUI's default easing.easeInOut (cubic-bezier(.4,0,.2,1)) and
    // duration.standard (300ms) already exactly match --transition - no
    // override needed.
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: 12, // --radius-sm
            borderWidth: "1.5px",
            boxShadow: "var(--shadow-sm)",
          },
        },
        variants: [
          {
            props: { variant: "contained", color: "primary" },
            style: {
              background: `linear-gradient(135deg, ${pink[500]}, ${pink[600]})`,
              boxShadow: "0 8px 20px rgba(255,90,154,.3), inset 0 1px 0 rgba(255,255,255,.25)",
              "&:hover": {
                background: `linear-gradient(135deg, ${pink[600]}, ${pink[700]})`,
                boxShadow: "0 12px 28px rgba(255,90,154,.4), inset 0 1px 0 rgba(255,255,255,.25)",
              },
            },
          },
          {
            props: { variant: "outlined", color: "primary" },
            style: {
              color: pink[600],
              borderColor: "rgba(255,90,154,.5)",
              background: "transparent",
              backdropFilter: "blur(8px)",
              "&:hover": { background: "rgba(255,90,154,.1)", borderColor: pink[600], color: pink[700] },
            },
          },
        ],
      },
    },
  });
}
